import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { useFormikContext } from 'formik';
import { useToastContext } from '@/common/components/ui/toast/toast.context';
import { ICompany } from '@/types/company.type';

interface LeadGoal {
  hour: string;
  goalQuantity: number;
}

interface LeadGoalsFieldProps {
  name?: string;
}

export const LeadGoalsField = ({ name = "leadGoals" }: LeadGoalsFieldProps) => {
  const { values, setFieldValue } = useFormikContext<ICompany>();
  const [tempGoal, setTempGoal] = useState<LeadGoal>({
    hour: '',
    goalQuantity: 0
  });

  const { notifyError } = useToastContext();

  const leadGoals: LeadGoal[] = values[name] || [];


  const handleAddGoal = () => {
    if (!tempGoal.hour || tempGoal.goalQuantity <= 0) return;

    if (leadGoals.some(g => g.hour === tempGoal.hour)) {
      notifyError('Já existe uma meta configurada para este horário');
      return;
    }

    setFieldValue(name, [...leadGoals, tempGoal]);

    setTempGoal({ hour: '', goalQuantity: 0 });
  };

  const handleRemoveGoal = (index: number) => {
    const newGoals = leadGoals.filter((_, i) => i !== index);
    setFieldValue(name, newGoals);
  };

  const getTotalGoals = () => {
    return leadGoals.reduce((sum, goal) => sum + Number(goal.goalQuantity), 0);
  };

  const generateHourOptions = () => {
    const hours: string[] = [];
    for (let h = 0; h < 24; h++) {
      const hh = String(h).padStart(2, '0');
      hours.push(`${hh}:00`);
    }
    return hours;
  };

  const hourOptions = generateHourOptions();

  return (
    <section className="bg-gray2/20 mt-6 mb-10 p-4 rounded-md shadow-lg">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-gray-800">Metas de Leads por Hora</h2>
        {leadGoals.length > 0 && (
          <div className="text-sm text-gray-600 bg-white px-4 py-2 rounded-md shadow">
            Total de Leads: <span className="font-bold text-lg text-black">{getTotalGoals()}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end mb-4">
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Hora:
          </label>
          <select
            value={tempGoal.hour}
            onChange={(e) => setTempGoal(prev => ({ ...prev, hour: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
          >
            <option value="">Selecione...</option>
            {hourOptions.map(hour => (
              <option key={hour} value={hour}>
                {hour}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Meta de Leads:
          </label>
          <input
            type="number"
            min="1"
            value={tempGoal.goalQuantity || ''}
            onChange={(e) => setTempGoal(prev => ({ ...prev, goalQuantity: Number(e.target.value) }))}
            placeholder="Ex: 50"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
          />
        </div>

        <Button 
          type="button" 
          onClick={handleAddGoal}
          className="flex items-center gap-2"
        >
          <span className="text-xl">+</span> Adicionar
        </Button>
      </div>

      {leadGoals.length === 0 ? (
        <div className="text-center py-8 text-gray-500 bg-white rounded-md">
          Nenhuma meta configurada ainda. Adicione horários e metas para começar.
        </div>
      ) : (
        <div className="space-y-2">
          {leadGoals
            .sort((a, b) => a.hour.localeCompare(b.hour))
            .map((goal, index) => (
              <div 
                key={index} 
                className="flex items-center justify-between p-4 bg-white rounded-md shadow-sm hover:shadow-md transition-shadow border border-gray-200 h-[40px]"
              >
                <div className="flex items-center gap-6">
                  <span className="font-semibold text-lg">
                    {goal.hour}
                  </span>
                  <div className="border-l-2 border-gray-300 pl-4">
                    <span className="text-sm text-gray-500">Meta: </span>
                    <span className="text-xl">
                      {goal.goalQuantity}
                    </span>
                    <span className="text-sm text-gray-500 ml-1">leads</span>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  type="button" 
                  onClick={() => handleRemoveGoal(index)}
                  className="hover:bg-red-50 h-[20px]"
                >
                  <Trash2 className="h-5 w-5 text-red-500" />
                </Button>
              </div>
            ))}
        </div>
      )}
    </section>
  );
}
